/**
 * "No customer email on file" — the staff-only send gate (2026-10-06, after #1759).
 *
 * approve-and-send refuses with 422 NO_CUSTOMER_EMAIL when every recipient is
 * one of ours (a staff address or the office box): the customer would get
 * nothing. Job #072654693 (Cintas, 7-Eleven #32912) went to corbin@ alone.
 *
 * The office then either goes back and adds the customer's address, records
 * that the customer already has it ("Already sent another way", 2026-10-07:
 * portal, mail, by hand), or ticks the TEST-copy box, which replays the send
 * with `staffOnlyConfirmed: true`. A test copy marks nothing delivered. Its
 * button stays dead until the box is ticked, so it is always deliberate.
 *
 * Sits inside a Dialog or inline in one, like DroppedAttachmentsWarning.
 */
import { useState } from "react";
import { AlertTriangle, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

/** The server's refusal, as the clients read it. */
export interface NoCustomerEmailRefusal {
    message: string;
    recipients: string[];
}

/** Pull a NO_CUSTOMER_EMAIL refusal out of an ApiError, or null. */
export function noCustomerEmailRefusal(e: unknown): NoCustomerEmailRefusal | null {
    const err = e as { status?: number; body?: { error?: string; message?: string; recipients?: unknown } } | null;
    if (err?.status !== 422 || err.body?.error !== "NO_CUSTOMER_EMAIL") return null;
    return {
        message: err.body.message ?? "No customer email on file.",
        recipients: Array.isArray(err.body.recipients) ? err.body.recipients.map(String) : [],
    };
}

export function StaffOnlySendGate({
    refusal,
    isPending,
    onSendStaffOnly,
    onSentAnotherWay,
    onCancel,
}: {
    refusal: NoCustomerEmailRefusal;
    isPending?: boolean;
    /** Replay the send with `staffOnlyConfirmed: true` (a TEST copy). */
    onSendStaffOnly: () => void;
    /** Open "Already sent another way" (2026-10-07). Omitted = not offered. */
    onSentAnotherWay?: () => void;
    /** Back out and add the customer's address. */
    onCancel: () => void;
}) {
    const [internal, setInternal] = useState(false);
    return (
        <>
            <div className="space-y-3">
                <div className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="space-y-0.5">
                        <p className="font-medium">No customer email on file</p>
                        <p className="text-xs text-amber-800/90 dark:text-amber-200/90">
                            Every recipient is one of ours
                            {refusal.recipients.length > 0 ? ` (${refusal.recipients.join(", ")})` : ""}, so the
                            customer would get nothing. Nothing was sent.
                            {onSentAnotherWay
                                ? " Did the customer already get it by portal, mail or by hand? Record that. Otherwise add their email."
                                : " Add the customer's email, or send a test copy to us on purpose."}
                        </p>
                    </div>
                </div>
                <label className="flex items-start gap-2 text-sm">
                    <input
                        type="checkbox"
                        className="mt-1"
                        checked={internal}
                        onChange={(e) => setInternal(e.target.checked)}
                        disabled={isPending}
                        aria-label="Send a test copy to staff only"
                    />
                    <span>
                        Send a <b>test copy</b> to staff only.{" "}
                        <span className="text-muted-foreground">
                            The customer gets nothing and nothing is marked delivered.
                        </span>
                    </span>
                </label>
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={onCancel} disabled={isPending}>
                    Add the customer&apos;s email
                </Button>
                <Button variant="outline" onClick={onSendStaffOnly} disabled={!internal || isPending}>
                    {isPending ? (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    ) : (
                        <Send className="mr-1.5 h-4 w-4" />
                    )}
                    Send test copy
                </Button>
                {onSentAnotherWay && (
                    <Button onClick={onSentAnotherWay} disabled={isPending} data-testid="gate-sent-another-way">
                        Already sent another way…
                    </Button>
                )}
            </div>
        </>
    );
}
